import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import printPdf from 'utils/printPdf';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

import { Field, reduxForm } from 'redux-form';

import CheckboxField from 'components/fields/CheckboxField';
import IconButton from 'components/common/IconButton';
import IconCheckboxSelect from 'components/icons/IconCheckboxSelect';
import IconCheckboxChecked from 'components/icons/IconCheckboxChecked';

import documentToPDF from 'utils/documentToPDF';
import templateToPDF from 'utils/templateToPDF';
import patientProfileToPDF from 'utils/patientProfileToPDF';
import patientInsuranceToPDF from 'utils/patientInsuranceToPDF';
import patientVitalsToPDF from 'utils/patientVitalsToPDF';
import splitOrderLines from 'utils/splitOrderLines';
import getDocumentFieldNames from 'utils/getDocumentFieldNames';
import getCurrentAppDocuments from 'utils/getCurrentAppDocuments';
import documents from 'config/documents';

const styles = theme => ({
  root: {
    padding: '0 24px',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 8,
      paddingRight: 8,
    },
  },
  gridItem: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  gridItemTitle: {
    marginTop: 10,
    marginBottom: 3,
  },
  gridItemPadding: {
    paddingLeft: 32,
  },
  section: {
    padding: '16px 0 24px 0',
    '& + &': {
      borderTop: `1px solid ${theme.palette.primary.A400}`,
    },
  },
  iconCheckboxSelect: {
    width: 18,
    height: 18,
    color: theme.palette.primary.A400,
  },
  iconCheckboxChecked: {
    width: 18,
    height: 18,
    color: theme.palette.primary[500],
  },
  iconButton: {
    margin: '-5px 0 -5px -14px',
  },
});

function onSubmit(values, dispatch, props) {
  return Object.keys(values).reduce((accumulator, currentValue) => {
    if (values[currentValue] === true) {
      if (currentValue.indexOf('Document') !== -1) {
        const documentID = Number(currentValue.split('_')[1]);
        const orderLine = currentValue.split('_')[2]
          ? currentValue.split('_')[2].split('-').map(item => Number(item))
          : null;
        const selectedDocument = documents
          .find(item => item.ID === documentID);
        return accumulator.then(prevPDF => documentToPDF({
          inputPDF: prevPDF,
          document: selectedDocument,
          patient: props.patient,
          diagnosis: props.diagnosis,
          business: props.business,
          systemSettings: props.systemSettings,
          facility: props.facility,
          insurance: props['Primary Insurance'],
          orderLine: orderLine
            ? props['Order Line'].filter(item => orderLine.indexOf(item.ID) !== -1)
            : props['Order Line'],
          appInfo: props.appInfo,
          orderInsuranceParam: props['Order Insurance Param'],
          prescriber: props.prescriber,
          orderNotes: (props.order && props.order.Notes) ? props.order.Notes : [],
          account: props.account,
        }));
      }
      if (currentValue.indexOf('Template') !== -1) {
        const templateID = Number(currentValue.split('_')[1]);
        const selectedTemplate = props.templates
          .find(item => item.ID === templateID);
        return accumulator.then(prevPDF => templateToPDF({
          inputPDF: prevPDF,
          template: selectedTemplate,
          patient: props.patient,
          order: props.order,
          business: props.business,
        }));
      }
      if (currentValue.indexOf('Patient Profile') !== -1) {
        return accumulator.then(prevPDF => patientProfileToPDF({
          inputPDF: prevPDF,
          patient: props.patient,
        }));
      }
      if (currentValue.indexOf('Patient Insurance') !== -1) {
        return accumulator.then(prevPDF => patientInsuranceToPDF({
          inputPDF: prevPDF,
          patient: props.patient,
        }));
      }
      if (currentValue.indexOf('Patient Vitals') !== -1) {
        return accumulator.then(prevPDF => patientVitalsToPDF({
          inputPDF: prevPDF,
          patient: props.patient,
        }));
      }
    }
    return accumulator;
  }, Promise.resolve());
}

function onSubmitSuccess(values) {
  const blob = values.output('blob');
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, 'documents.pdf');
  } else {
    printPdf(blob);
  }
}

const PrintDialogForm = ({
  templates,
  change,
  formValues,
  appInfo,
  classes,
  ...otherProps
}) => {
  const currentAppDocuments = getCurrentAppDocuments(documents, appInfo);
  const patientDataFields = [
    'Patient Profile',
    'Patient Insurance',
    'Patient Vitals',
    getDocumentFieldNames(otherProps['Order Line'],
      currentAppDocuments.filter(item => ['Form 1500'].indexOf(item.Type) !== -1)),
  ];
  const priorAuthorizationTemplatesFields = getDocumentFieldNames(otherProps['Order Line'],
    currentAppDocuments.filter(item => item.Type === 'Prior Authorization Template'));
  const generalDocumentsFields = getDocumentFieldNames(otherProps['Order Line'],
    currentAppDocuments.filter(item => item.Type === 'General'));
  const deliveryTicketsFields = getDocumentFieldNames(otherProps['Order Line'],
    currentAppDocuments.filter(item => ['Delivery Ticket'].indexOf(item.Type) !== -1));
  const assessmentFormsFields = getDocumentFieldNames(otherProps['Order Line'],
    currentAppDocuments.filter(item => ['Assessment Form'].indexOf(item.Type) !== -1));
  const cmsFields = currentAppDocuments.filter(item => item.Type === 'CMS')
    .map(item => `Document_${item.ID}_${otherProps['Order Line'].map(orderLine => orderLine.ID).join('-')}`);
  const templateFields = templates.map(item => `Template_${item.ID}`);
  const allFieldsSelected = fields =>
    fields.filter(field => formValues[field]).length
      === fields.length;
  const onSelectAllClick = (fields) => {
    if (allFieldsSelected(fields)) {
      fields.forEach(item => change(item, false));
    } else {
      fields.forEach(item => change(item, true));
    }
  };
  return (
    <div className={classes.root}>
      <section className={classes.section}>
        <Grid
          container
          spacing={24}
        >
          <Grid
            item
            xs={6}
          >
            <Grid
              container
              spacing={0}
              direction="column"
            >
              <Grid item>
                <Grid
                  container
                  spacing={0}
                  direction="row"
                  alignItems="center"
                >
                  <Grid item>
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => onSelectAllClick(patientDataFields)}
                      tooltipContent="Select All"
                    >
                      {allFieldsSelected(patientDataFields) ? (
                        <IconCheckboxChecked className={classes.iconCheckboxChecked} />
                      ) : (
                        <IconCheckboxSelect className={classes.iconCheckboxSelect} />
                      )}
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <Typography color="secondary">
                      Patient Data
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item className={classes.gridItemPadding}>
                <Field
                  id="Patient Profile"
                  name="Patient Profile"
                  label="Patient Profile"
                  component={CheckboxField}
                />
              </Grid>
              <Grid item className={classes.gridItemPadding}>
                <Field
                  id="Patient Insurance"
                  name="Patient Insurance"
                  label="Patient Insurance"
                  component={CheckboxField}
                />
              </Grid>
              <Grid item className={classes.gridItemPadding}>
                <Field
                  id="Patient Vitals"
                  name="Patient Vitals"
                  label="Patient Vitals"
                  component={CheckboxField}
                />
              </Grid>
              {currentAppDocuments.filter(item => ['Form 1500'].indexOf(item.Type) !== -1).map((item) => {
                if (otherProps['Order Line'].length > 0 && item.OrderLineMax) {
                  const splitedOrderLines = splitOrderLines(otherProps['Order Line'], item.OrderLineMax);
                  return (
                    <Fragment key={item.ID}>
                      {splitedOrderLines.map((orderLines) => {
                        const orderLineIDs = orderLines.map(orderLine => orderLine.ID).join('-');
                        const orderLineHCPCSCodes = orderLines.map(orderLine => orderLine['HCPC S Code']['HCPC S Code']).join(', ');
                        return (
                          <Grid
                            className={classNames(classes.gridItem, classes.gridItemPadding)}
                            key={orderLineIDs}
                            item
                          >
                            <Field
                              id={`Document_${item.ID}_${orderLineIDs}`}
                              name={`Document_${item.ID}_${orderLineIDs}`}
                              label={splitedOrderLines.length > 1
                                ? `${item.Name} (${orderLineHCPCSCodes})`
                                : item.Name}
                              component={CheckboxField}
                            />
                          </Grid>
                        );
                      })}
                    </Fragment>
                  );
                }
                return (
                  <Grid
                    className={classNames(classes.gridItem, classes.gridItemPadding)}
                    item
                    key={item.ID}
                  >
                    <Field
                      id={`Document_${item.ID}`}
                      name={`Document_${item.ID}`}
                      component={CheckboxField}
                      label={item.Name}
                    />
                  </Grid>
                );
              })}


        <Grid className={classes.gridItemTitle} item>
                <Grid
                  container
                  spacing={0}
                  direction="row"
                  alignItems="center"
                >
                  <Grid item>
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => onSelectAllClick(generalDocumentsFields)}
                      tooltipContent="Select All"
                    >
                      {allFieldsSelected(generalDocumentsFields) ? (
                        <IconCheckboxChecked className={classes.iconCheckboxChecked} />
                      ) : (
                        <IconCheckboxSelect className={classes.iconCheckboxSelect} />
                      )}
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <Typography color="secondary">
                      General Documents
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {currentAppDocuments.filter(item => item.Type === 'General').map((item) => {
                if (otherProps['Order Line'].length > 0 && item.OrderLineMax) {
                  const splitedOrderLines = splitOrderLines(otherProps['Order Line'], item.OrderLineMax);
                  return (
                    <Fragment key={item.ID}>
                      {splitedOrderLines.map((orderLines) => {
                        const orderLineIDs = orderLines.map(orderLine => orderLine.ID).join('-');
                        const orderLineHCPCSCodes = orderLines.map(orderLine => orderLine['HCPC S Code']['HCPC S Code']).join(', ');
                        return (
                          <Grid
                            key={orderLineIDs}
                            item
                            className={classes.gridItemPadding}
                          >
                            <Field
                              id={`Document_${item.ID}_${orderLineIDs}`}
                              name={`Document_${item.ID}_${orderLineIDs}`}
                              label={splitedOrderLines.length > 1
                                ? `${item.Name} (${orderLineHCPCSCodes})`
                                : item.Name}
                              component={CheckboxField}
                            />
                          </Grid>
                        );
                      })}
                    </Fragment>
                  );
                }
                return (
                  <Grid
                    className={classNames(classes.gridItem, classes.gridItemPadding)}
                    item
                    key={item.ID}
                  >
                    <Field
                      id={`Document_${item.ID}`}
                      name={`Document_${item.ID}`}
                      component={CheckboxField}
                      label={item.Name}
                    />
                  </Grid>
                );
              })}



              <Grid className={classes.gridItemTitle} item>
                <Grid
                  container
                  spacing={0}
                  direction="row"
                  alignItems="center"
                >
                  <Grid item>
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => onSelectAllClick(priorAuthorizationTemplatesFields)}
                      tooltipContent="Select All"
                    >
                      {allFieldsSelected(priorAuthorizationTemplatesFields) ? (
                        <IconCheckboxChecked className={classes.iconCheckboxChecked} />
                      ) : (
                        <IconCheckboxSelect className={classes.iconCheckboxSelect} />
                      )}
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <Typography color="secondary">
                      Prior Authorization Templates
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {currentAppDocuments.filter(item => item.Type === 'Prior Authorization Template').map((item) => {
                if (otherProps['Order Line'].length > 0 && item.OrderLineMax) {
                  const splitedOrderLines = splitOrderLines(otherProps['Order Line'], item.OrderLineMax);
                  return (
                    <Fragment key={item.ID}>
                      {splitedOrderLines.map((orderLines) => {
                        const orderLineIDs = orderLines.map(orderLine => orderLine.ID).join('-');
                        const orderLineHCPCSCodes = orderLines.map(orderLine => orderLine['HCPC S Code']['HCPC S Code']).join(', ');
                        return (
                          <Grid
                            key={orderLineIDs}
                            item
                            className={classes.gridItemPadding}
                          >
                            <Field
                              id={`Document_${item.ID}_${orderLineIDs}`}
                              name={`Document_${item.ID}_${orderLineIDs}`}
                              label={splitedOrderLines.length > 1
                                ? `${item.Name} (${orderLineHCPCSCodes})`
                                : item.Name}
                              component={CheckboxField}
                            />
                          </Grid>
                        );
                      })}
                    </Fragment>
                  );
                }
                return (
                  <Grid
                    className={classNames(classes.gridItem, classes.gridItemPadding)}
                    item
                    key={item.ID}
                  >
                    <Field
                      id={`Document_${item.ID}`}
                      name={`Document_${item.ID}`}
                      component={CheckboxField}
                      label={item.Name}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
          <Grid
            item
            xs={6}
          >
            <Grid
              container
              spacing={0}
              direction="column"
            >
              <Grid item>
                <Grid
                  container
                  spacing={0}
                  direction="row"
                  alignItems="center"
                >
                  <Grid item>
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => onSelectAllClick(deliveryTicketsFields)}
                      tooltipContent="Select All"
                    >
                      {allFieldsSelected(deliveryTicketsFields) ? (
                        <IconCheckboxChecked className={classes.iconCheckboxChecked} />
                      ) : (
                        <IconCheckboxSelect className={classes.iconCheckboxSelect} />
                      )}
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <Typography color="secondary">
                      Delivery Forms
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {currentAppDocuments.filter(item => ['Delivery Ticket'].indexOf(item.Type) !== -1).map((item) => {
                if (otherProps['Order Line'].length > 0 && item.OrderLineMax) {
                  const splitedOrderLines = splitOrderLines(otherProps['Order Line'], item.OrderLineMax);
                  return (
                    <Fragment key={item.ID}>
                      {splitedOrderLines.map((orderLines) => {
                        const orderLineIDs = orderLines.map(orderLine => orderLine.ID).join('-');
                        const orderLineHCPCSCodes = orderLines.map(orderLine => orderLine['HCPC S Code']['HCPC S Code']).join(', ');
                        return (
                          <Grid
                            className={classNames(classes.gridItem, classes.gridItemPadding)}
                            key={orderLineIDs}
                            item
                          >
                            <Field
                              id={`Document_${item.ID}_${orderLineIDs}`}
                              name={`Document_${item.ID}_${orderLineIDs}`}
                              label={splitedOrderLines.length > 1
                                ? `${item.Name} (${orderLineHCPCSCodes})`
                                : item.Name}
                              component={CheckboxField}
                            />
                          </Grid>
                        );
                      })}
                    </Fragment>
                  );
                }
                return (
                  <Grid
                    className={classNames(classes.gridItem, classes.gridItemPadding)}
                    item
                    key={item.ID}
                  >
                    <Field
                      id={`Document_${item.ID}`}
                      name={`Document_${item.ID}`}
                      component={CheckboxField}
                      label={item.Name}
                    />
                  </Grid>
                );
              })}
              <Grid item>
                <Grid
                  container
                  spacing={0}
                  direction="row"
                  alignItems="center"
                >
                  <Grid item>
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => onSelectAllClick(assessmentFormsFields)}
                      tooltipContent="Select All"
                    >
                      {allFieldsSelected(assessmentFormsFields) ? (
                        <IconCheckboxChecked className={classes.iconCheckboxChecked} />
                      ) : (
                        <IconCheckboxSelect className={classes.iconCheckboxSelect} />
                      )}
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <Typography color="secondary">
                      Assessment Forms
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {currentAppDocuments.filter(item => ['Assessment Form'].indexOf(item.Type) !== -1).map((item) => {
                if (otherProps['Order Line'].length > 0 && item.OrderLineMax) {
                  const splitedOrderLines = splitOrderLines(otherProps['Order Line'], item.OrderLineMax);
                  return (
                    <Fragment key={item.ID}>
                      {splitedOrderLines.map((orderLines) => {
                        const orderLineIDs = orderLines.map(orderLine => orderLine.ID).join('-');
                        const orderLineHCPCSCodes = orderLines.map(orderLine => orderLine['HCPC S Code']['HCPC S Code']).join(', ');
                        return (
                          <Grid
                            className={classNames(classes.gridItem, classes.gridItemPadding)}
                            key={orderLineIDs}
                            item
                          >
                            <Field
                              id={`Document_${item.ID}_${orderLineIDs}`}
                              name={`Document_${item.ID}_${orderLineIDs}`}
                              label={splitedOrderLines.length > 1
                                ? `${item.Name} (${orderLineHCPCSCodes})`
                                : item.Name}
                              component={CheckboxField}
                            />
                          </Grid>
                        );
                      })}
                    </Fragment>
                  );
                }
                return (
                  <Grid
                    className={classNames(classes.gridItem, classes.gridItemPadding)}
                    item
                    key={item.ID}
                  >
                    <Field
                      id={`Document_${item.ID}`}
                      name={`Document_${item.ID}`}
                      component={CheckboxField}
                      label={item.Name}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </Grid>
      </section>
      <section className={classes.section}>
        <Grid
          container
          spacing={24}
        >
          <Grid
            item
            xs={6}
          >
            <Grid
              container
              spacing={0}
              direction="column"
            >
              <Grid item>
                <Grid
                  container
                  spacing={0}
                  direction="row"
                  alignItems="center"
                >
                  <Grid item>
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => onSelectAllClick(cmsFields)}
                      tooltipContent="Select All"
                    >
                      {allFieldsSelected(cmsFields) ? (
                        <IconCheckboxChecked className={classes.iconCheckboxChecked} />
                      ) : (
                        <IconCheckboxSelect className={classes.iconCheckboxSelect} />
                      )}
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <Typography color="secondary">
                      CMSs
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {currentAppDocuments.filter(item => item.Type === 'CMS').map(item => (
                <Grid
                  className={classNames(classes.gridItem, classes.gridItemPadding)}
                  item
                  key={item.ID}
                >
                  <Field
                    id={`Document_${item.ID}`}
                    name={`Document_${item.ID}_${otherProps['Order Line'].map(orderLine => orderLine.ID).join('-')}`}
                    component={CheckboxField}
                    label={item.Name}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid
            item
            xs={6}
          >
            <Grid
              container
              spacing={0}
              direction="column"
            >
              <Grid item>
                <Grid
                  container
                  spacing={0}
                  direction="row"
                  alignItems="center"
                >
                  <Grid item>
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => onSelectAllClick(templateFields)}
                      tooltipContent="Select All"
                    >
                      {allFieldsSelected(templateFields) ? (
                        <IconCheckboxChecked className={classes.iconCheckboxChecked} />
                      ) : (
                        <IconCheckboxSelect className={classes.iconCheckboxSelect} />
                      )}
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <Typography color="secondary">
                      Templates
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {templates.map(item => (
                <Grid
                  item
                  key={item.ID}
                  className={classes.gridItemPadding}
                >
                  <Field
                    id={`Template_${item.ID}`}
                    name={`Template_${item.ID}`}
                    component={CheckboxField}
                    label={item.Name}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </section>
    </div>
  );
};

PrintDialogForm.propTypes = {
  change: PropTypes.func.isRequired,
  templates: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })).isRequired,
  'Order Line': PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })).isRequired,
  formValues: PropTypes.shape({
    Data: PropTypes.object,
    Documents: PropTypes.object,
    Templates: PropTypes.object,
  }),
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    gridItem: PropTypes.string.isRequired,
    gridItemTitle: PropTypes.string.isRequired,
    section: PropTypes.string.isRequired,
  }).isRequired,
};

PrintDialogForm.defaultProps = {
  'Order Line': [],
  formValues: {},
};

export default reduxForm({
  onSubmit,
  onSubmitSuccess,
})(withStyles(styles)(PrintDialogForm));



// WEBPACK FOOTER //
// ./src/components/common/PrintDialog/PrintDialogForm.js